import { useState } from 'react';
import styled from 'styled-components';
import { ChevronRight } from 'lucide-react';
import { articlesForCollection, collectionsWithArticles, searchHelp } from '../help/helpContent';
import { CollectionIcon } from '../../help/CollectionIcon';
import { ArticleItem, SearchWrapper, SearchIcon, SearchInput, SectionHead } from '../supportShared';
import { useGoToHelpArticle } from '../useGoToHelpArticle';

const Pad = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
`;

// Fixed two columns, per the mockup. Safe without a stacking rule because the
// panel is viewport-clamped (`width: min(384px, 100vw - 32px)` in
// SupportPanel) rather than full-bleed — it never gets wide enough for a third
// column, and on a narrow phone both tiles shrink together.
// `grid-auto-rows: 1fr` makes every row as tall as the tallest, so a wrapping
// label ("Clients & caseloads") does not leave its row taller than the others.
const CollectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: 1fr;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const CollectionCard = styled.button`
  display: flex;
  align-items: center;
  /* Mockup's tile gap; no token is 10px (spacing[2] 8, spacing[3] 12). */
  gap: 10px;
  text-align: left;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  /* Mockup's --r. No token is 10px (sm 4, md 6, lg 8, xl 12), and the tile is
     deliberately rounder than the 8px icon square nested inside it. */
  border-radius: 10px;
  /* 12px == spacing[3]; the 11px inline padding has no token. */
  padding: ${({ theme }) => theme.spacing[3]} 11px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  /* The mockup's tile height, kept literal — no spacing token is 56px. It is
     a floor, not the final height: border-box makes 12px padding + the 32px
     icon square + 1px borders come to 58px, exactly as in the mockup. */
  min-height: 56px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

// Lets a long label wrap inside the tile instead of stretching the column.
const CollectionLabel = styled.span`
  min-width: 0;
`;

// The tinted square behind the icon. The glyph itself comes from the shared
// `CollectionIcon`, so the widget and the full-page Knowledge Center never
// drift apart. `flex: none` keeps the square from being squashed when the
// label beside it wraps.
const IconSlot = styled.span`
  flex: none;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.backgrounds.tealTint};
  color: ${({ theme }) => theme.colors.primary.base};

  /* The mockup's lighter stroke; lucide's own default is 2. Scoped here so
     the shared CollectionIcon stays untouched for the full-page cards. */
  svg {
    stroke-width: 1.75;
  }
`;

const ArticleText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ArticleSnippet = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  margin-top: 2px;
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary.base};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing[2]};

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyState = styled.div`
  color: ${({ theme }) => theme.colors.text.light};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

export function HelpTab() {
  const goToArticle = useGoToHelpArticle();
  const [query, setQuery] = useState('');
  const [activeCollection, setActiveCollection] = useState<string | null>(null);

  const searchResults = query.trim() ? searchHelp(query) : [];
  const collectionArticles = activeCollection ? articlesForCollection(activeCollection) : [];

  return (
    <Pad>
      <SearchWrapper>
        <SearchIcon aria-hidden="true" />
        <SearchInput
          type="search"
          placeholder="Search for help"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveCollection(null);
          }}
          aria-label="Search for help"
        />
      </SearchWrapper>

      {query.trim() ? (
        <>
          <SectionHead>Search results</SectionHead>
          {searchResults.length === 0 && <EmptyState>No articles matched &quot;{query}&quot;.</EmptyState>}
          {searchResults.map((a) => (
            <ArticleItem key={a.id} onClick={() => goToArticle(a.id)}>
              <ArticleText>
                {a.title}
                <ArticleSnippet>{a.summary}</ArticleSnippet>
              </ArticleText>
              <ChevronRight size={14} />
            </ArticleItem>
          ))}
        </>
      ) : activeCollection ? (
        <>
          <BackLink type="button" onClick={() => setActiveCollection(null)}>
            ‹ All collections
          </BackLink>
          {collectionArticles.map((a) => (
            <ArticleItem key={a.id} onClick={() => goToArticle(a.id)}>
              <ArticleText>
                {a.title}
                <ArticleSnippet>{a.summary}</ArticleSnippet>
              </ArticleText>
              <ChevronRight size={14} />
            </ArticleItem>
          ))}
        </>
      ) : (
        <>
          <SectionHead>Browse by topic</SectionHead>
          <CollectionGrid>
            {/* Only topics that have an article — the panel has no way to
                express a "Coming soon" card, so an empty one is a dead end. */}
            {collectionsWithArticles().map((c) => (
              <CollectionCard key={c.id} type="button" onClick={() => setActiveCollection(c.id)}>
                <IconSlot>
                  {/* 17px here, not the shared default of 19px, which the
                      larger full-page topic cards rely on. */}
                  <CollectionIcon iconKey={c.iconKey} size={17} />
                </IconSlot>
                <CollectionLabel>{c.title}</CollectionLabel>
              </CollectionCard>
            ))}
          </CollectionGrid>
        </>
      )}
    </Pad>
  );
}

export default HelpTab;
